<template>
  <div class="mt-4">
    <p class="text-caption text-grey-body">
      See what each
      <a href="/background-checks/whats-covered" target="_blank" class="text-primary underline primary-focus">badge covers</a> and
      learn more about our commitment to safety by visiting the
      <a href="/trust-and-safety" target="_blank" class="text-primary underline primary-focus">Trust & Safety center</a>.
    </p>

    <div v-for="(badge, index) in visibleBadges" :key="index" class="flex gap-4 mt-16">
      <component :is="badge.graphic" :width="60" :height="60" :active="badge.active" />
      <help-icon v-if="badge.flagged" class="absolute left-60 md-1:left-68" color="#282930" :aria-hidden="true" />
      <div>
        <p class="text-strong-body text-grey-header">{{ badge.title }}</p>
        <p class="flex flex-col text-caption" :class="getBadgeTextClasses(badge)">
          <span v-if="badge.completedAtText">{{ badge.completedAtText }}</span>
          <span v-if="badge.text">{{ badge.text }}</span>
        </p>
        <poppins-button
          v-if="badge.manageable"
          value="Get a copy"
          href=" https://candidate.checkr.com/login#login"
          variant="text"
        />
      </div>
    </div>

    <poppins-button
      :value="btnData.value"
      :href="btnData.href"
      variant="outlined"
      size="medium"
      class="mt-16 w-full"
      @click="handleTrustSafetyButtonClick"
    />
    <p class="text-caption text-grey-body mt-16">*BGC+ includes everything in BGC, plus additional checks.</p>
    <p v-if="!editModeActive" class="text-caption text-grey-body mt-16">
      We encourage parents to conduct their own screenings including using the background check tools.
    </p>
  </div>
</template>

<script lang="ts">
import PoppinsButton from '@/components/button/Button.vue';
import IDVBadge from '@/graphics/badges/IDVBadge.vue';
import BackgroundCheckBadge from '@/graphics/badges/BackgroundCheckBadge.vue';
import EnhancedBackgroundCheckBadge from '@/graphics/badges/EnhancedBackgroundCheckBadge.vue';
import MotorVehicleRecordBadge from '@/graphics/badges/MotorVehicleRecordBadge.vue';
import HelpIcon from '@/graphics/icons/HelpIcon.vue';
import { sendSegmentEvent } from '@/lib/segment';

interface Badge {
  completedAt: Date;
  expiresAt: Date;
  name: string;
  status: string;
}

interface IDV {
  latestUpdate: Date;
  provider: string;
  status: string;
  verdict: string;
}

interface BadgeDisplay {
  key: string;
  graphic: any;
  title: string;
  text: string;
  completedAtText?: string;
  active?: boolean;
  manageable?: boolean;
  flagged?: boolean;
}

export default {
  name: 'ProfileTrustAndSafety',
  components: {
    PoppinsButton,
    HelpIcon,
  },
  props: {
    badges: {
      type: Array as () => Badge[],
      default: () => [],
    },
    idv: {
      type: Object as () => IDV,
      default: () => ({}),
    },
    viewerAccountData: {
      type: Object,
      default: () => ({}),
    },
    isProfileOwner: {
      type: Boolean,
      default: false,
    },
    sitterId: {
      type: String,
      default: '',
    },
    accountTypeInfo: {
      default: () => ({}),
      type: Object,
    },
    editModeActive: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    btnData(): { value: string; href: string } {
      return !this.viewerLoggedIn
        ? { value: 'Log in', href: `/login?return_path=${encodeURIComponent(this.$route.fullPath)}` }
        : {
            value: 'Run new check',
            href: this.isProfileOwner
              ? '/account/providers/background-checks'
              : `/background-check/sponsored-checks/${this.sitterId}/new`,
          };
    },
    identityVerificationPassed(): boolean {
      return this.idv?.verdict === 'passed';
    },
    viewerLoggedIn(): boolean {
      return this.viewerAccountData !== null;
    },
    visibleBadges(): BadgeDisplay[] {
      const badgeConfig = [
        {
          key: 'idv',
          graphic: IDVBadge,
          title: 'Identity verified',
          text: this.identityVerificationPassed ? 'Completed document verification.' : 'Not complete.',
          active: this.identityVerificationPassed,
        },
        {
          key: 'backgroundCheck',
          graphic: BackgroundCheckBadge,
          title: 'Basic criminal check',
          ...this.getBadgeDisplayData('basic_criminal'),
        },
        {
          key: 'enhancedBackgroundCheck',
          graphic: EnhancedBackgroundCheckBadge,
          title: '*Enhanced criminal check',
          ...this.getBadgeDisplayData('enhanced_criminal'),
        },
        {
          key: 'motorVehicleRecord',
          graphic: MotorVehicleRecordBadge,
          title: 'Motor vehicle records',
          ...this.getBadgeDisplayData('motor_vehicle'),
        },
      ];

      // Hide badge details if viewer is not logged in
      return badgeConfig.map((badge) => ({
        ...badge,
        completedAtText: this.viewerLoggedIn ? (badge.completedAtText ?? '') : '',
        text: this.viewerLoggedIn ? (badge.text ?? '') : 'Log in to view details',
      }));
    },
  },
  methods: {
    trackTrustAndSafetyLogin(): void {
      sendSegmentEvent('Trust and Safety Login Clicked', {
        sitter_uuid: this.$route.params.sitter_uuid,
        care_type: this.$route.params.care_type,
      });
    },
    trackParentTrustAndSafetyLogin(): void {
      sendSegmentEvent('Profile Trust and Safety Login Clicked', {
        sitter_uuid: this.$route.params.sitter_uuid,
        care_type: this.$route.params.care_type,
      });
    },
    trackParentRunCheckClick(): void {
      sendSegmentEvent('Run Check Clicked', {
        sitter_uuid: this.$route.params.sitter_uuid,
        care_type: this.$route.params.care_type,
      });
    },
    handleTrustSafetyButtonClick(): void {
      if (!this.viewerLoggedIn) {
        this.trackTrustAndSafetyLogin();
      } else if (this.accountTypeInfo.isParent && this.btnData.value === 'Run new check') {
        this.trackParentRunCheckClick();
      }
    },
    getBadgeDisplayData(badgeName: string): object {
      const badge = this.badges?.find((b) => b.name === badgeName);

      const badgeDetails = {
        status: badge?.status || '',
        completedDate: badge?.completedAt ? new Date(badge.completedAt).toLocaleDateString() : '',
        expirationDate: badge?.expiresAt ? new Date(badge.expiresAt).toLocaleDateString() : '',
      };

      const completedAtText = `Completed ${badgeDetails.completedDate}.`;

      switch (badgeDetails.status) {
        case 'PENDING':
          return {
            text: 'Pending. Check for results soon.',
            active: false,
          };
        case 'COMPLETE':
          return {
            completedAtText,
            text: this.editModeActive ? 'Parents may ask to see a copy.' : 'Contact the caregiver directly to see the report.',
            active: true,
            manageable: this.editModeActive,
          };
        case 'REVIEW_RECOMMENDED':
          return {
            completedAtText,
            text: this.editModeActive
              ? 'Eligible for badge with information review. Parents may ask to see a copy.'
              : 'Badge has been awarded, but we recommend contacting the sitter to see results.',
            active: true,
            flagged: true,
            manageable: this.editModeActive,
          };
        case 'INELIGIBLE':
          return {
            completedAtText,
            text: this.editModeActive
              ? 'This check was completed, and you did not meet the requirements to receive the badge.'
              : 'This check was completed, and the caregiver did not meet requirements to receive the badge.',
            active: false,
            ineligible: true,
            manageable: this.editModeActive,
          };
        case 'EXPIRED':
          return {
            text: `Expired on ${badgeDetails.expirationDate}.`,
            active: false,
            manageable: this.editModeActive,
          };
        default:
          return {
            text: 'Never run.',
            active: false,
          };
      }
    },
    getBadgeTextClasses(badge): string {
      if (!this.viewerLoggedIn) return 'text-grey-body italic';
      return badge.ineligible ? 'text-critical-contrast' : 'text-grey-body';
    },
  },
};
</script>
